import React, { useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import useLockBodyScroll from '../hooks/useLockBodyScroll';

// Plugin para dibujar el porcentaje en el centro del doughnut ANTES del tooltip
const centerTextPlugin = {
  id: 'centerText',
  beforeDraw(chart) {
    const { ctx, chartArea: area } = chart;
    if (!area) return;
    const dataset = chart.data.datasets[0];
    const total = dataset.data.reduce((a, b) => a + b, 0);
    const aciertos = dataset.data[0];
    const porcentaje = total > 0 ? Math.round((aciertos / total) * 100) : 0;
    let color = '#22c55e';
    if (porcentaje < 25) color = '#ef4444';
    else if (porcentaje <= 50) color = '#f59e42';
    ctx.save();
    ctx.font = 'bold 2.5rem Segoe UI, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(`${porcentaje}%`, (area.left + area.right) / 2, (area.top + area.bottom) / 2);
    ctx.restore();
  }
};

Chart.register(ArcElement, Tooltip, Legend);

// Nuevo modal: muestra gráfico de aciertos/fallos y porcentaje
const ConfirmationDialog = ({
  mostrarDialogoConfirmacion,
  setMostrarDialogoConfirmacion,
  onConfirm,
  puntuacion = 0,
  fallos = 0,
  totalPreguntas = 0,
  embedded = false
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!mostrarDialogoConfirmacion) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (!dialogRef.current) {
        return;
      }

      if (!dialogRef.current.contains(event.target)) {
        setMostrarDialogoConfirmacion(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [mostrarDialogoConfirmacion, setMostrarDialogoConfirmacion]);

  useLockBodyScroll(mostrarDialogoConfirmacion && !embedded);

  if (!mostrarDialogoConfirmacion) return null;

  // Calcular no respondidas correctamente
  const respondidas = puntuacion + fallos;
  const noRespondidas = totalPreguntas - respondidas;
  const data = {
    labels: ['Aciertos', 'Fallos', 'No respondidas'],
    datasets: [
      {
        data: [puntuacion, fallos, noRespondidas],
        backgroundColor: [
          '#22c55e', // verde
          '#ef4444', // rojo
          '#6b7280'  // gris
        ],
        borderWidth: 6, // Más ancho para separación visible
        borderJoinStyle: 'round',
        borderAlign: 'inner',
        borderColor: '#232b36', // Fondo del gráfico para separar
      },
    ],
  };

  const options = {
    cutout: '70%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}`;
          }
        },
        // Forzar el tooltip por encima del porcentaje
        z: 9999,
        // Si no funciona, el fallback es CSS global
      }
    },
    elements: {
      arc: {
        borderRadius: 12 // Bordes redondeados
      }
    }
  };

  return (
    <div
      className={
        embedded
          ? 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 sm:relative sm:inset-auto sm:z-auto sm:bg-transparent sm:p-0 sm:w-full'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3'
      }
    >
      <div
        ref={dialogRef}
        className={`bg-white rounded-lg shadow-lg ${embedded ? 'p-4' : 'p-6'} w-full ${embedded ? '' : 'max-w-md'} dark:bg-gray-800 dark:text-white transition-colors duration-300 flex flex-col items-center`}
      >
        <h3 className="text-lg font-semibold mb-4 dark:text-white">Resumen del Concurso</h3>
        <div className="relative flex flex-col items-center justify-center w-full mb-4" style={{maxWidth: 200, maxHeight: 200, minHeight: 200}}>
          <Doughnut data={data} options={options} style={{ width: 200, height: 200 }} plugins={[centerTextPlugin]} />
        </div>
        <div className="flex justify-end space-x-4 w-full">
          <button
            onClick={() => setMostrarDialogoConfirmacion(false)}
            className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors dark:bg-gray-600 dark:hover:bg-gray-700 duration-300"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              onConfirm();
              setMostrarDialogoConfirmacion(false);
            }}
            className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors dark:bg-red-700 dark:hover:bg-red-800 duration-300"
          >
            Terminar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
